import React, { useCallback, useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  BarChart3,
  CopyCheck,
  CreditCard,
  FileText,
  History,
  LayoutDashboard,
  LogOut,
  Menu,
  Moon,
  Receipt,
  ScanLine,
  Settings,
  Sun,
  Users,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useI18n } from "../../i18n";
import { DuplicatesApi } from "../../lib/api";
import { cn } from "../../lib/utils";
import LanguageSwitcher from "../LanguageSwitcher";
import NotificationBell from "../NotificationBell";
import ClientSwitcher from "../ClientSwitcher";
import { Button } from "../ui/button";
import { Sheet, SheetContent, SheetTrigger } from "../ui/sheet";

const NAV = [
  { to: "/app/dashboard", labelKey: "nav.dashboard", icon: LayoutDashboard, testId: "nav-dashboard",
    roles: ["admin", "accountant"] },
  { to: "/app/scan", labelKey: "nav.scan", icon: ScanLine, testId: "nav-scan", roles: ["admin"] },
  { to: "/app/receipts", labelKey: "nav.receipts", icon: Receipt, testId: "nav-receipts",
    roles: ["admin", "accountant"] },
  { to: "/app/duplicates", labelKey: "nav.duplicates", icon: CopyCheck, testId: "nav-duplicates",
    roles: ["admin"], badge: "duplicates" },
  { to: "/app/reports", labelKey: "nav.reports", icon: FileText, testId: "nav-reports",
    roles: ["admin", "accountant"] },
  { to: "/app/team", labelKey: "nav.team", icon: Users, testId: "nav-team", roles: ["admin"] },
  { to: "/app/settings", labelKey: "nav.settings", icon: Settings, testId: "nav-settings",
    roles: ["admin"] },
  { to: "/app/audit", labelKey: "nav.audit", icon: History, testId: "nav-audit",
    roles: ["admin"] },
  { to: "/app/billing", labelKey: "nav.billing", icon: CreditCard, testId: "nav-billing",
    roles: ["admin"] },
];

const Brand = () => (
  <Link to="/app/dashboard" className="flex items-center gap-2" data-testid="brand-link">
    <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground">
      <BarChart3 className="h-4 w-4" />
    </span>
    <span className="font-display text-base font-semibold tracking-tight">LedgerLens</span>
  </Link>
);

export const AdminShell = ({ title, subtitle, actions, children }) => {
  const { user, tenant, logout, isAdmin } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [duplicates, setDuplicates] = useState(0);
  const [dark, setDark] = useState(() => localStorage.getItem("ll_theme") === "dark");

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("ll_theme", dark ? "dark" : "light");
  }, [dark]);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const loadDuplicates = useCallback(() => {
    if (!isAdmin) return;
    DuplicatesApi.count()
      .then((d) => setDuplicates(d.count || 0))
      .catch(() => setDuplicates(0));
  }, [isAdmin]);

  useEffect(() => {
    loadDuplicates();
    const timer = setInterval(loadDuplicates, 20000);
    return () => clearInterval(timer);
  }, [loadDuplicates, location.pathname]);

  const role = user?.role || "employee";
  const items = NAV.filter((item) => item.roles.includes(role));

  const NavItems = ({ onNavigate }) => (
    <nav className="space-y-1">
      {items.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          onClick={onNavigate}
          data-testid={item.testId}
          className={({ isActive }) =>
            cn(
              "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
              isActive
                ? "bg-primary/10 text-primary"
                : "text-muted-foreground hover:bg-accent hover:text-foreground"
            )
          }
        >
          <item.icon className="h-4 w-4" />
          <span className="flex-1">{t(item.labelKey)}</span>
          {item.badge === "duplicates" && duplicates > 0 ? (
            <span
              className="ll-num rounded-full bg-amber-100 px-1.5 text-xs font-semibold text-amber-800"
              data-testid="nav-duplicates-badge"
            >
              {duplicates}
            </span>
          ) : null}
        </NavLink>
      ))}
    </nav>
  );

  return (
    <div className="min-h-screen bg-background">
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-[260px] flex-col border-r bg-card px-4 py-5 lg:flex">
        <Brand />
        <div className="mt-6 flex-1">
          <NavItems />
        </div>
        <div className="space-y-3 border-t pt-4">
          <LanguageSwitcher className="w-full justify-center" />
          <div className="px-1">
            <p className="truncate text-sm font-medium">{tenant?.name}</p>
            <p className="truncate text-xs text-muted-foreground">{user?.email}</p>
            {role === "accountant" ? (
              <span
                className="mt-1 inline-block rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground"
                data-testid="readonly-badge-sidebar"
              >
                {t("nav.readOnly")}
              </span>
            ) : null}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="flex-1"
              onClick={() => {
                logout();
                navigate("/login");
              }}
              data-testid="logout-button"
            >
              <LogOut className="mr-2 h-3.5 w-3.5" /> {t("common.signOut")}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              aria-label="Toggle dark mode"
              onClick={() => setDark((d) => !d)}
              data-testid="theme-toggle"
            >
              {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      </aside>

      <div className="lg:pl-[260px]">
        <header className="sticky top-0 z-20 border-b bg-card/95 backdrop-blur">
          <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 px-4 py-3 sm:px-6 lg:px-8">
            <div className="flex min-w-0 grow basis-3/5 items-center gap-2 sm:gap-3">
              <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                <SheetTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="-ml-2 shrink-0 lg:hidden"
                    data-testid="mobile-nav-toggle"
                  >
                    <Menu className="h-5 w-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-[260px] p-4">
                  <Brand />
                  <div className="mt-6">
                    <NavItems onNavigate={() => setMobileOpen(false)} />
                  </div>
                  <div className="mt-6 space-y-3">
                    <LanguageSwitcher className="w-full justify-center" />
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={() => {
                        logout();
                        navigate("/login");
                      }}
                    >
                      <LogOut className="mr-2 h-3.5 w-3.5" /> {t("common.signOut")}
                    </Button>
                  </div>
                </SheetContent>
              </Sheet>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <h1 className="truncate font-display text-lg font-semibold" data-testid="page-title">
                    {title}
                  </h1>
                  {role === "accountant" ? (
                    <span
                      className="shrink-0 rounded-full border bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground"
                      data-testid="readonly-badge"
                    >
                      {t("nav.readOnly")}
                    </span>
                  ) : null}
                </div>
                {subtitle ? (
                  <p className="truncate text-xs text-muted-foreground">{subtitle}</p>
                ) : null}
              </div>
            </div>
            <div className="ml-auto flex min-w-0 flex-wrap items-center justify-end gap-2 sm:gap-2.5">
              <ClientSwitcher />
              <NotificationBell />
              {actions}
            </div>
          </div>
        </header>
        <main className="px-4 py-5 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-[1400px]">{children}</div>
        </main>
      </div>
    </div>
  );
};

export default AdminShell;
